import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';

export function useViewTemplates({
  workflowTemplateId,
  type,
  subtype,
  name,
  isPublic,
  excludeSubtypes,
  limit,
  skip,
}: {
  workflowTemplateId?: string | null;
  type?: API.ViewType;
  subtype?: API.ViewSubtype;
  name?: string;
  isPublic?: boolean;
  excludeSubtypes?: API.ViewSubtype[];
  limit?: number;
  skip?: boolean;
}): {
  viewTemplates: API.ViewTemplateFragment[];
  hasMore: boolean;
  loading: boolean;
  moreLoading: boolean;
  fetchMore: () => void;
  error?: Error;
} {
  const [moreLoading, setMoreLoading] = useState(false);
  const [offsetUpdated, setOffsetUpdated] = useState(false);
  const queryVariables = {
    input: {
      workflowTemplateId: workflowTemplateId ?? '',
      type,
      subtype,
      isPublic,
      excludeSubtypes,
      name,
      limit,
      sort: {
        field: 'CREATED_AT' as API.ListViewTemplateSortField,
        direction: 'ASC' as API.SortDirection,
      },
    },
  };

  const {
    data,
    loading,
    error,
    fetchMore: fetchMoreTemplates,
  } = useQuery<API.ListViewTemplatesQueryV2Query, API.ListViewTemplatesQueryV2QueryVariables>(
    Queries.viewTemplate.listViewTemplatesV2,
    {
      variables: queryVariables,
      skip: skip || isNil(workflowTemplateId),
    },
  );

  const offset = useMemo(() => {
    setOffsetUpdated(true);
    return data?.listViewTemplatesV2?.viewTemplates?.length ?? 0;
  }, [data?.listViewTemplatesV2?.viewTemplates?.length]);

  const fetchMore = useCallback(() => {
    if (data?.listViewTemplatesV2?.hasMore && !loading && !moreLoading && offsetUpdated) {
      setOffsetUpdated(false);
      setMoreLoading(true);
      fetchMoreTemplates({
        variables: {
          input: {
            ...queryVariables.input,
            offset: offset,
          },
        },
      }).finally(() => {
        setMoreLoading(false);
      });
    }
  }, [
    data?.listViewTemplatesV2?.hasMore,
    fetchMoreTemplates,
    loading,
    moreLoading,
    offset,
    offsetUpdated,
    queryVariables.input,
  ]);

  const viewTemplates = useMemo(
    () => (data?.listViewTemplatesV2?.viewTemplates ?? []) as API.ViewTemplateFragment[],
    [data?.listViewTemplatesV2?.viewTemplates],
  );

  return {
    viewTemplates,
    hasMore: data?.listViewTemplatesV2?.hasMore ?? false,
    fetchMore,
    loading,
    moreLoading,
    error,
  };
}
